import React, { useContext, useEffect, useState } from 'react';
import styles from './farmTransportationFlow.module.scss';
import CardWithStickyHeader, {
  ButtonProps,
} from 'shared/components/CardWithStickyHeader';
import {
  actionButtons,
  farmStagesTitles,
} from 'constants/FarmboxConstants/stage0.constants';
import { ArrowRightOutlined } from '@ant-design/icons';
import { ButtonType } from 'enums/ButtonEnums';
import {
  STAGE_2,
  TRANSPORTATION_FIELD_STAGE2,
} from 'constants/FarmboxConstants/stage2.constants';
import {
  FieldPath,
  SubmitHandler,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { TransportationDetails } from 'models/Farmbox/transportation.model';
import FarmTransportationService from 'services/FarmTransportationService/farmTransportation.service';
import { useParams, useSearchParams } from 'react-router-dom';
import CreateSupplierModal from 'shared/components/CreateSupplierModal';
import { TransportationConstants } from 'views/CreateProductFlow/Transportation/TransportationConstants';
import { convertToOptions } from 'views/CreateProductFlow/RawMaterial/RawMaterialHelper';
import TransportationFormSection from './farmTransportationHelper';
import useFarmboxRedirections from 'shared/utils/useFarmboxRedirections';
import isProductDisabled from 'shared/utils/isProductDisabled';
import { ambientTemperature } from 'constants/constants';
import { HelpDeskContext } from 'context/HelpDeskContext';
import SupplierService from 'services/ValueChainService/supplier.service';

interface SupplierModalState {
  isOpen: boolean;
  rowIndex: number;
  childIndex?: number;
  formType?: TRANSPORTATION_FIELD_STAGE2;
}

const FarmTransportation = () => {
  const {
    loading: suppliersLoading,
    fetchSuppliers,
    supplierList,
    createSupplier,
  } = SupplierService();
  const {
    loading: transportationLoading,
    updateTransportationData,
    fetchTransportationData,
    transportationData,
    buttonLoading,
  } = FarmTransportationService();
  const { redirectToFarmFuelAndEnergy, redirectToFarmRawMaterial } =
    useFarmboxRedirections();

  const { id: commodityId, status } = useParams();
  const { setIsHelpDeskOpen } = useContext(HelpDeskContext);
  const [searchParams] = useSearchParams();
  const queryParams = Object.fromEntries(searchParams.entries());
  const { control, setValue, handleSubmit, reset } =
    useFormContext<TransportationDetails>();
  const [modalState, setModalState] = useState<SupplierModalState>({
    isOpen: false,
    rowIndex: -1,
  });

  useEffect(() => {
    fetchSuppliers(limitParam);
    fetchTransportationData({ commodityId });
  }, []);

  // // For updating form default values
  useEffect(() => {
    const defaultValues =
      transportationData?.fertilizers || transportationData?.packagings
        ? Object.assign(new TransportationDetails(), transportationData)
        : new TransportationDetails();

    defaultValues.fertilizers?.forEach(item => {
      item.temperatureControl ??= ambientTemperature;
      item.children?.forEach(children => {
        children.temperatureControl ??= ambientTemperature;
      });
    });
    reset(defaultValues);
  }, [transportationData]);

  const { TRANSPORTATION } = farmStagesTitles;
  const { PRIMARY } = ButtonType;
  const { NEXT } = actionButtons;
  const { HEADING } = STAGE_2;
  const { FERTILIZER, PACKAGING } = HEADING;
  const limitParam = { size: 250 };
  const { USER_DETAILS } = TransportationConstants;
  const user = localStorage.getItem(USER_DETAILS) || '{}';
  const parsedUser = !!user ? JSON.parse(user) : '';
  const supplierOptions = convertToOptions(supplierList ?? []);
  const isDisabled = isProductDisabled(status);

  const { fields: fertilizerFields } = useFieldArray({
    control,
    name: TRANSPORTATION_FIELD_STAGE2.FERTILIZER,
  });
  const { fields: packagingFields } = useFieldArray({
    control,
    name: TRANSPORTATION_FIELD_STAGE2.PACKAGING,
  });

  const handleFormSubmit: SubmitHandler<TransportationDetails> = async (
    formData: TransportationDetails
  ) => {
    if (!commodityId && formData.fertilizers?.[0]?.name) {
      return;
    }
    if (!formData?.fertilizers?.length && !formData?.packagings?.length) {
      return;
    }
    if (!isDisabled) {
      const success = await updateTransportationData(commodityId, formData);
      if (success)
        redirectToFarmFuelAndEnergy(Number(commodityId), status, queryParams);
    } else {
      redirectToFarmFuelAndEnergy(Number(commodityId), status, queryParams);
    }
  };

  const actionButton: ButtonProps[] = [
    {
      icon: <ArrowRightOutlined />,
      title: NEXT,
      clickCallback: () => {
        handleSubmit(handleFormSubmit)();
      },
      type: PRIMARY,
      submit: true,
      loading: buttonLoading,
    },
  ];

  const handleCloseSupplierModal = () =>
    setModalState({ isOpen: false, rowIndex: -1 });

  const handleOpenSupplierModal = (
    rowIndex: number,
    childIndex?: number,
    formType?: TRANSPORTATION_FIELD_STAGE2
  ) => {
    setModalState({
      isOpen: true,
      rowIndex,
      childIndex,
      formType,
    });
  };
  const handleFertilizerModalOpen = (index: number, childIndex?: number) => {
    handleOpenSupplierModal(
      index,
      childIndex,
      TRANSPORTATION_FIELD_STAGE2.FERTILIZER
    );
  };

  const handlePackagingModalOpen = (index: number, childIndex?: number) => {
    handleOpenSupplierModal(
      index,
      childIndex,
      TRANSPORTATION_FIELD_STAGE2.PACKAGING
    );
  };

  const handleAddSupplier = async (supplierId: string) => {
    if (!supplierId) return;
    await fetchSuppliers?.(limitParam);
    if (supplierId) {
      const { rowIndex, childIndex, formType } = modalState;
      const fieldPath =
        childIndex !== undefined
          ? `${formType}.${rowIndex}.children.${childIndex}.supplierId`
          : `${formType}.${rowIndex}.supplierId`;
      setValue(
        fieldPath as FieldPath<TransportationDetails>,
        Number(supplierId),
        {
          shouldTouch: true,
          shouldValidate: true,
        }
      );
    }
  };
  const handleBackNavigation = () => {
    setIsHelpDeskOpen(false);
    redirectToFarmRawMaterial(Number(commodityId), status, queryParams);
  };

  return (
    <div className={styles.transportationContainer}>
      <div className={styles.transportationCardContainer}>
        <CardWithStickyHeader
          title={TRANSPORTATION}
          actionButtons={actionButton}
          loading={transportationLoading}
          backCallback={handleBackNavigation}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <TransportationFormSection
              title={FERTILIZER}
              fields={fertilizerFields}
              formType={TRANSPORTATION_FIELD_STAGE2.FERTILIZER}
              supplierOptions={supplierOptions}
              onOpenSupplierModal={handleFertilizerModalOpen}
              isDisabled={isDisabled}
            />
            <TransportationFormSection
              title={PACKAGING}
              fields={packagingFields}
              formType={TRANSPORTATION_FIELD_STAGE2.PACKAGING}
              supplierOptions={supplierOptions}
              onOpenSupplierModal={handlePackagingModalOpen}
              isUnderlined
              isDisabled={isDisabled}
            />
          </form>
        </CardWithStickyHeader>

        <CreateSupplierModal
          showModalState={modalState.isOpen}
          handleModalCancel={handleCloseSupplierModal}
          handleModalConfirm={handleCloseSupplierModal}
          createSupplier={createSupplier}
          id={parsedUser.id}
          successCallback={handleAddSupplier}
          loading={suppliersLoading}
        />
      </div>
    </div>
  );
};

export default FarmTransportation;
